/** Run a trace: pick a context, get per-node verdicts.
 *
 *  Collapsed by default. The tree is the page's subject; tracing is a thing you
 *  reach for, and an always-open panel pushed the tree below the fold. The
 *  summary keeps the result visible while collapsed, so collapsing never hides
 *  the fact that a trace is applied.
 *
 *  Reuses the test-render tool's `ContextSourcePicker` verbatim, so "which
 *  context" means the same three things everywhere in the notification suite
 *  (pasted JSON / a delivery log / an entity id).
 *
 *  Beyond the button, the panel's job is to keep the result honest: it restates
 *  what the trace refused to answer (`trace.notes`) rather than leaving a reader
 *  to infer that a dimmed loop body "did not fire".
 */
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { NotificationAnatomy } from '@/api/notificationAnatomy';

import {
  buildEvaluateContext,
  ContextSourcePicker,
  contextSourceComplete,
  EMPTY_CONTEXT_SOURCE,
  type ContextSourceValue,
  type EntityKind,
} from '../notifications/test-render/ContextSourcePicker';

interface Props {
  /** Event family, for the entity-id field's label. */
  entityFamily: string;
  entityKind: EntityKind | null;
  running: boolean;
  error: string | null;
  /** The trace block of the current payload, when one has been run. */
  trace: NotificationAnatomy['trace'];
  onRun(context: ReturnType<typeof buildEvaluateContext>): void;
  onClear(): void;
}

export function TracePanel({
  entityFamily,
  entityKind,
  running,
  error,
  trace,
  onRun,
  onClear,
}: Props) {
  const [source, setSource] = useState<ContextSourceValue>(EMPTY_CONTEXT_SOURCE);
  // Opens itself when a trace is running or has failed, so neither state can be
  // hidden behind a collapsed summary.
  const [open, setOpen] = useState(false);
  const expanded = open || running || Boolean(error);
  const ready = contextSourceComplete(source, entityKind);

  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, next) => setOpen(next)}
      sx={{ mb: 2, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">Trace against a context</Typography>
          {trace?.available ? (
            <Chip
              size="small"
              color="success"
              label={`${trace.evaluations?.toLocaleString() ?? '?'} expressions evaluated`}
              sx={{ height: 20, fontSize: '0.6875rem' }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              which branches were actually taken
            </Typography>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Evaluates every condition against the context a test render would use, and marks which
          branches were taken. Nothing is sent and nothing is saved.
        </Typography>

        {/* A column, not a row: the picker is two stacked controls whose second
         *  one carries a floating label, and a bare flex row collided them. */}
        <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
          <ContextSourcePicker
            value={source}
            onChange={setSource}
            entityKind={entityKind}
            entityFamily={entityFamily}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="contained"
              startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              disabled={!ready || running}
              onClick={() => onRun(buildEvaluateContext(source))}
            >
              {running ? 'Tracing…' : 'Trace'}
            </Button>
            {trace && (
              <Button size="small" startIcon={<ClearIcon />} onClick={onClear}>
                Clear
              </Button>
            )}
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}

        {trace && !trace.available && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {trace.reason ?? 'There was nothing to trace.'}
          </Alert>
        )}

        {trace?.available && (
          <Box sx={{ mt: 1.5 }}>
            {/* The count lives in the summary chip (visible while collapsed);
             *  repeating it here was just noise. This adds only what the chip
             *  cannot carry. */}
            {trace.traced?.length ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Traced {trace.traced.length} source
                {trace.traced.length === 1 ? '' : 's'}: {trace.traced.join(', ')}.
              </Typography>
            ) : null}
            {/* Stated, not implied: a reader must not take "in loop" for "did not fire". */}
            {trace.notes?.map((note, i) => (
              <Typography
                key={i}
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                · {note}
              </Typography>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
